import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleModal from 'components/SimpleModal';
import ApiClientManager from 'core/apiClient';
import { useNotification } from 'hooks/useNotification';
import { selectAuthToken } from 'selectors';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import { setTeamAiEnabledApi } from 'TeamModule/api/setTeamAiEnabledApi';
import type { ActionMenuItem } from 'components/ActionMenu';

export interface OpenSetTeamAiEnabledModalArgs {
  tenant: string;
  realmId: string;
  teamId: number;
  teamName: string;
  /** The state the team is being switched to. */
  aiEnabled: boolean;
  /**
   * Called after galaxy-server accepted the change, e.g. to refetch the row.
   * A returned promise is awaited before the change counts as settled.
   */
  onSuccess?: () => unknown;
  /** True while the change is being applied (request and `onSuccess`), then false. */
  onPendingChange?: (pending: boolean) => void;
}

const onOff = (aiEnabled: boolean) => (aiEnabled ? 'On' : 'Off');

/**
 * The team page's Turn AI Suggestions On / Off menu entry. It offers the
 * opposite of the team's current state and is disabled (and inert) for users
 * below Sales/CSM, while the team's state is unknown (`aiEnabled` undefined:
 * the team data is still loading or describes another team), and while the
 * team is blocked, which it says.
 */
export const buildAiEnabledMenuItem = ({
  aiEnabled,
  aiBlocked,
  enabled,
  onSelect,
}: {
  aiEnabled: boolean | undefined;
  aiBlocked: boolean | undefined;
  enabled: boolean;
  onSelect: (aiEnabled: boolean) => void;
}): ActionMenuItem => {
  const isOn = Boolean(aiEnabled) && !aiBlocked;
  const label = `Turn AI Suggestions ${onOff(!isOn)}`;
  if (aiBlocked) {
    return {
      label: (
        <>
          {label}
          <Box component="span" sx={{ display: 'block', color: 'text.secondary' }}>
            Blocked - unblock first
          </Box>
        </>
      ),
      disabled: true,
    };
  }
  const usable = enabled && aiEnabled !== undefined;
  return {
    label,
    disabled: !usable,
    onClick: usable ? () => onSelect(!isOn) : undefined,
  };
};

/**
 * Confirm-then-apply modal behind the team page's AI Suggestions On / Off action.
 * Galaxy's AI Suggestions setting controls AI work for the team (embeddings,
 * summaries, AI reasoning on suggestions); Mosaic still shows suggestion
 * screens either way.
 */
export const useSetTeamAiEnabledModal = () => {
  const { openModal } = useDynamicModal();
  const token = useSelector(selectAuthToken);
  const { showSuccess, showWarning, showError } = useNotification();
  // One change at a time, so toasts and the final state follow the last click.
  const inFlight = useRef(false);

  const openSetTeamAiEnabledModal = useCallback(
    ({
      tenant,
      realmId,
      teamId,
      teamName,
      aiEnabled,
      onSuccess,
      onPendingChange,
    }: OpenSetTeamAiEnabledModalArgs) => {
      const label = `${teamName} (${teamId})`;
      // Empty headers fall back to the page's tenant/realm: the change would
      // land on another team's realm.
      if (!tenant || !realmId) {
        showError(
          `Could not turn AI suggestions ${onOff(aiEnabled)} for ${label}: the team has no tenant or realm.`,
        );
        return;
      }
      if (inFlight.current) {
        showWarning(
          `The last AI suggestions change for ${label} is still being applied. Try again when it finishes.`,
        );
        return;
      }
      /** Applies the change and reports it; true when galaxy-server accepted it. */
      const apply = async (authToken: string) => {
        try {
          const { data } = await setTeamAiEnabledApi({
            params: { teamId, aiEnabled },
            token: authToken,
            config: { headers: ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId }) },
          });
          if (data.searchTeamUpdated) {
            showSuccess(`AI suggestions turned ${onOff(aiEnabled)} for ${label}.`);
          } else {
            showWarning(
              `AI suggestions turned ${onOff(aiEnabled)} for ${label}, but the Teams list could not be updated yet. It catches up at the weekly sync.`,
            );
          }
          return true;
        } catch (e) {
          showError(
            `Could not turn AI suggestions ${onOff(aiEnabled)} for ${label}: ${extractApiErrorMessage(e)}`,
          );
          return false;
        }
      };
      const onConfirm = async () => {
        if (!token) {
          showError(
            `Could not turn AI suggestions ${onOff(aiEnabled)} for ${label}: you are signed out. Sign in again.`,
          );
          return;
        }
        inFlight.current = true;
        onPendingChange?.(true);
        try {
          if (await apply(token)) await onSuccess?.();
        } finally {
          inFlight.current = false;
          onPendingChange?.(false);
        }
      };

      openModal({
        modal: ({ toggle }) => (
          <SimpleModal
            open
            onClose={toggle}
            onConfirm={onConfirm}
            closeOnConfirm
            headerText={`Turn AI Suggestions ${onOff(aiEnabled)}`}
            confirmButtonText={`Turn ${onOff(aiEnabled)}`}
            Body={
              <div>
                Turn AI suggestions <strong>{onOff(aiEnabled)}</strong> for{' '}
                <strong>{teamName}</strong> (team {teamId}, realm {realmId})?{' '}
                {aiEnabled
                  ? 'AI work (embeddings, summaries, AI reasoning on suggestions) starts for this team.'
                  : 'AI work stops for this team; Mosaic keeps showing suggestions without AI reasoning.'}
              </div>
            }
          />
        ),
      });
    },
    [openModal, showError, showSuccess, showWarning, token],
  );

  return { openSetTeamAiEnabledModal };
};

export default useSetTeamAiEnabledModal;
